import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { Button } from "../components/ui/button";
import InstructorTrustStack from "../components/instructor/InstructorTrustStack";
import PupilAvailability from "../components/learner/PupilAvailability";
import axios from "axios";
import { toast } from "sonner";
import usePolicy from "../hooks/usePolicy";
import {
  ChevronLeft, ChevronRight, Clock, Check, X, Calendar,
  Star, MapPin, AlertTriangle, TrendingUp, CreditCard,
  ChevronDown, Info, Shield, Loader2, Repeat,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const STATUS_COLORS = {
  available: { bg: "bg-emerald-500", ring: "ring-emerald-300", text: "text-white", label: "Available" },
  gone: { bg: "bg-red-400", ring: "ring-red-200", text: "text-white", label: "Gone" },
  my_booking: { bg: "bg-blue-500", ring: "ring-blue-300", text: "text-white", label: "Your Lesson" },
  approval_required: { bg: "bg-amber-500", ring: "ring-amber-300", text: "text-white", label: "Ask Instructor" },
};

const DEMAND_COLORS = {
  open: "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800",
  limited: "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800",
  full: "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800",
  unavailable: "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700",
  approval_required: "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800",
};

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

export default function LearnerBooking() {
  const { user } = useAuth();
  const nav = useNavigate();
  const policy = usePolicy();
  const cancelHrs = policy.cancellation_window_hours;
  const payHrs = policy.payment_due_hours;

  const [view, setView] = useState("month"); // month | day
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calData, setCalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDuration, setBookingDuration] = useState(120);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [travelRec, setTravelRec] = useState(null);
  const [learnerPostcode, setLearnerPostcode] = useState("");
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showWhyExpanded, setShowWhyExpanded] = useState(false);
  // R3.1 — small click-to-toggle tooltip on the platform-transaction-fee
  // line. Click outside / re-click to dismiss.
  const [showFeeTooltip, setShowFeeTooltip] = useState(false);

  const [instructorId, setInstructorId] = useState(null);
  const [linkedInstructors, setLinkedInstructors] = useState([]);
  const [instructorError, setInstructorError] = useState(null);
  const [availabilityError, setAvailabilityError] = useState(null);
  const calendarRequest = useRef(0);
  const context = `${instructorId}:${year}:${month}:${bookingDuration}`;
  const currentContext = useRef(context);
  currentContext.current = context;

  // R1.2 — pricing quote (lesson + booking fee + total) fetched
  // whenever the duration / instructor changes. Backend single source of
  // truth; we never recompute the fee on the client.
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let active = true;
    setQuote(null);
    const fetchQuote = async () => {
      if (!instructorId || !bookingDuration) return;
      try {
        const { data } = await axios.get(
          `${API}/api/lesson-payments/quote?instructor_id=${instructorId}&duration=${bookingDuration}`,
          { withCredentials: true },
        );
        if (active) setQuote(data);
      } catch {
        if (active) setQuote(null);
      }
    };
    fetchQuote();
    return () => { active = false; };
  }, [instructorId, bookingDuration]);

  useEffect(() => {
    const findInstructor = async () => {
      try {
        const res = await axios.get(`${API}/api/bookings/instructors`, { withCredentials: true });
        const instructors = res?.data?.instructors || [];
        setLinkedInstructors(instructors);
        setInstructorId(instructors[0]?.id || null);
        setInstructorError(instructors.length ? null : "No active instructor relationship is linked to this account.");
      } catch (error) {
        setLinkedInstructors([]);
        setInstructorId(null);
        setInstructorError(error.response?.data?.detail || "Your instructor relationship could not be verified.");
      }
      // Fetch pricing config
      try {
        const { data } = await axios.get(`${API}/api/learning/booking-config`, { withCredentials: true });
        setPricingConfig(data);
      } catch {}
    };
    findInstructor();
  }, []);

  const fetchMonth = useCallback(async () => {
    if (!instructorId) return;
    const requestId = ++calendarRequest.current;
    const requestContext = `${instructorId}:${year}:${month}:${bookingDuration}`;
    const isCurrent = () => requestId === calendarRequest.current && currentContext.current === requestContext;
    setLoading(true);
    setCalData([]);
    setSelectedSlot(null);
    setPolicyAccepted(false);
    const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
    setAvailabilityError(null);
    try {
      const { data } = await axios.get(`${API}/api/learning/available-slots/${instructorId}?month=${monthStr}&duration=${bookingDuration}`, { withCredentials: true });
      if (!isCurrent()) return;
      setCalData(data.days || []);
    } catch (error) {
      if (!isCurrent()) return;
      setCalData([]);
      setAvailabilityError(error.response?.data?.detail || "Availability could not be loaded.");
    }
    if (isCurrent()) setLoading(false);
  }, [year, month, instructorId, bookingDuration]);

  useEffect(() => {
    fetchMonth();
    return () => { calendarRequest.current += 1; };
  }, [fetchMonth]);

  const clearBookingChoice = () => {
    setSelectedSlot(null);
    setSelectedDate(null);
    setPolicyAccepted(false);
    setTravelRec(null);
    setView("month");
  };

  const goMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m > 11) { m = 0; y++; }
    else if (m < 0) { m = 11; y--; }
    setMonth(m);
    setYear(y);
    setSelectedDate(null);
  };

  const openDay = (dateStr) => {
    setSelectedDate(dateStr);
    setView("day");
    setSelectedSlot(null);
    setTravelRec(null);
  };

  const fetchTravelRec = async (postcode) => {
    if (!postcode || postcode.length < 2) return;
    try {
      const { data } = await axios.post(`${API}/api/learning/travel-recommendation`, {
        postcode, test_centre: "kings_lynn",
      }, { withCredentials: true });
      setTravelRec(data);
      if (data.recommendation) {
        setBookingDuration(data.recommendation.recommended_duration);
      }
    } catch {}
  };

  const requestAdjustedSlot = async () => {
    const isOnlyTime = selectedSlot.exception_kind === "pupil_only_time";
    const { data } = await axios.post(`${API}/api/bookings/authenticated-request`, {
      date: selectedDate,
      start_time: selectedSlot.time,
      instructor_id: instructorId,
      notes: isOnlyTime
        ? "Pupil says this is their only workable time. It conflicts with the instructor’s geographic workflow and requires explicit approval."
        : `Journey-aware adjustment from ${selectedSlot.requested_time || selectedSlot.time}. Instructor approval required.`,
    }, { withCredentials: true });
    toast.success(data?.confirmed_at
      ? "Booking confirmed."
      : isOnlyTime
        ? "Exception requested — your instructor must approve it before anything changes."
        : "Adjusted time requested — awaiting your instructor’s approval.");
    if (Number(data?.remaining_to_minimum || 0) > 0) {
      toast.message(
        `Your place needs ${data.remaining_to_minimum} more future lesson${data.remaining_to_minimum === 1 ? "" : "s"} to reach the minimum of three.`
      );
      setSelectedSlot(null);
      setPolicyAccepted(false);
      await fetchMonth();
      return;
    }
    nav("/learn/bookings");
  };

  const bookSlot = async () => {
    if (!selectedSlot || !selectedDate || !policyAccepted) return;
    if (selectedSlot.status === "approval_required") {
      setCheckoutLoading(true);
      try { await requestAdjustedSlot(); }
      catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Request failed"); }
      finally { setCheckoutLoading(false); }
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/lesson-payments/checkout`, {
        duration: bookingDuration,
        date: selectedDate,
        start_time: selectedSlot.time,
        origin_url: window.location.origin,
        policy_accepted: true,
        instructor_id: instructorId,
      }, { withCredentials: true });

      if (data.founder_mode || data.no_charge) {
        toast.message(data.message || "Admin billing bypass — use a real test account.", { duration: 6000 });
        setCheckoutLoading(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setCheckoutLoading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
      setCheckoutLoading(false);
    }
  };

  // ── Pay-later: book lesson without Stripe. Same policy gate, no card. ──
  const [payLaterLoading, setPayLaterLoading] = useState(false);
  const bookSlotPayLater = async () => {
    if (!selectedSlot || !selectedDate || !policyAccepted) return;
    setPayLaterLoading(true);
    if (selectedSlot.status === "approval_required") {
      try { await requestAdjustedSlot(); }
      catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || "Request failed"); }
      finally { setPayLaterLoading(false); }
      return;
    }
    try {
      const { data } = await axios.post(`${API}/api/lesson-payments/book-pay-later`, {
        duration: bookingDuration,
        date: selectedDate,
        start_time: selectedSlot.time,
        instructor_id: instructorId,
        policy_accepted: true,
      }, { withCredentials: true });
      // Surface-1 Truth Hardening (Feb 2026): the "Booking confirmed"
      // wording is gated on the authoritative `confirmed_at` field.
      // If the backend cannot prove confirmation, fall back to the
      // canonical pre-confirmation language ("Awaiting confirmation").
      if (data?.confirmed_at) {
        toast.success("Booking confirmed — payment due. Your instructor will be in touch.");
      } else {
        toast.success("Booking requested — awaiting confirmation. Your instructor will be in touch.");
      }
      if (Number(data?.remaining_to_minimum || 0) > 0) {
        toast.message(
          `Keep booking: you need ${data.remaining_to_minimum} more future lesson${data.remaining_to_minimum === 1 ? "" : "s"} to secure the minimum of three.`
        );
        setSelectedSlot(null);
        setPolicyAccepted(false);
        await fetchMonth();
        return;
      }
      nav("/learn/bookings");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setPayLaterLoading(false);
    }
  };

  // Calendar grid
  const firstDay = new Date(year, month, 1);
  const startDow = (firstDay.getDay() + 6) % 7; // Monday-based
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().split("T")[0];

  const dayMap = {};
  for (const d of calData) dayMap[d.date] = d;

  const selectedDayData = selectedDate ? dayMap[selectedDate] : null;
  const selectedInstructor = linkedInstructors.find((item) => item.id === instructorId) || null;
  const readiness = selectedInstructor?.readiness || null;
  const readinessLabels = {
    ready_to_begin: "Ready to begin",
    ready_with_support: "Ready with support",
    instructor_check_required: "Instructor check needed",
    not_enough_information: "More preparation evidence needed",
  };
  const hasBookableDay = calData.some((day) =>
    day?.status && day.status !== "unavailable"
  );

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-8">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => view === "day" ? setView("month") : nav("/learn")} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 flex-1">{view === "month" ? "Book a Lesson" : `${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}`}</h1>
          {view === "month" && (
            <button data-testid="recurring-link" onClick={() => nav("/learn/book/recurring")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 text-blue-600 text-xs font-bold">
              <Repeat className="w-3.5 h-3.5" />Weekly
            </button>
          )}
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 mt-4">

        {instructorError && (
          <div data-testid="booking-instructor-error" className="rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 p-4 mb-3 text-sm">
            {instructorError} Please contact the instructor before making a booking.
          </div>
        )}

        {linkedInstructors.length > 1 && (
          <label className="block rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 mb-3 text-sm font-bold text-slate-700 dark:text-slate-200">
            Choose your instructor
            <select
              value={instructorId || ""}
              onChange={(event) => {
                clearBookingChoice();
                setCalData([]);
                setQuote(null);
                setInstructorId(event.target.value);
              }}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-2 text-slate-900"
            >
              {linkedInstructors.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </label>
        )}

        {instructorId && <PupilAvailability instructorId={instructorId}
          noSuitableTimes={!loading && !availabilityError && !calData.some(day =>
            (day.slots || []).some(slot => ["available", "approval_required"].includes(slot.status)))}
          onSaved={() => { clearBookingChoice(); fetchMonth(); }} />}

        {instructorId && (
          <div className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 mb-3" data-testid="booking-instructor-trust-card">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Your instructor</p>
            <p className="text-sm font-extrabold text-slate-900 dark:text-white mb-2">
              {selectedInstructor?.name || "Your instructor"}
            </p>
            <InstructorTrustStack instructorId={instructorId} max={5} />
            {readiness && (
              <div
                data-testid="booking-readiness-summary"
                className="mt-3 rounded-xl bg-emerald-50 p-3 text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-100"
              >
                <p className="text-[10px] font-extrabold uppercase tracking-widest">Your preparation</p>
                <p className="mt-1 text-sm font-extrabold">
                  {readinessLabels[readiness.status] || "Preparation recorded"}
                </p>
                <p className="mt-1 text-xs">
                  Homework: {String(readiness.preparation || "not recorded").replaceAll("_", " ")}
                  {" · "}Understanding: {String(readiness.understanding || "not checked").replaceAll("_", " ")}
                </p>
                <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-200">
                  Your instructor will still confirm practical progress in the car.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═══ WEEK BUBBLES ═══ */}
        {view === "month" && (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          // Compute Monday of this week
          const dow = today.getDay();   // 0 Sun ... 6 Sat
          const offsetToMon = dow === 0 ? -6 : 1 - dow;
          const monday = new Date(today);
          monday.setDate(today.getDate() + offsetToMon);
          const bubbles = [
            { offset: 0, label: "This week" },
            { offset: 1, label: "Next week" },
            { offset: 2, label: "+2 weeks" },
            { offset: 3, label: "+3 weeks" },
          ];
          const goToWeek = (offsetWeeks) => {
            const targetMon = new Date(monday);
            targetMon.setDate(monday.getDate() + offsetWeeks * 7);
            setYear(targetMon.getFullYear());
            setMonth(targetMon.getMonth());
            setSelectedDate(null);
          };
          return (
            <div data-testid="week-bubbles" className="mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Jump to</p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {bubbles.map((b) => {
                  const targetMon = new Date(monday);
                  targetMon.setDate(monday.getDate() + b.offset * 7);
                  const sameMonth = targetMon.getFullYear() === year && targetMon.getMonth() === month;
                  return (
                    <button
                      key={b.offset}
                      type="button"
                      data-testid={`week-bubble-${b.offset}`}
                      onClick={() => goToWeek(b.offset)}
                      className={`flex-none px-3.5 py-2 rounded-full text-[12px] font-extrabold transition-colors ${
                        sameMonth
                          ? "bg-slate-900 text-white"
                          : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:border-slate-400"
                      }`}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ═══ MONTH VIEW ═══ */}
        {view === "month" && (
          <div className="space-y-4">
            {/* Month nav */}
            <div className="flex items-center justify-between">
              <button data-testid="prev-month" onClick={() => goMonth(-1)} className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"><ChevronLeft className="w-5 h-5 text-slate-500" /></button>
              <h2 className="text-xl font-extrabold text-slate-900">{MONTHS[month]} {year}</h2>
              <button data-testid="next-month" onClick={() => goMonth(1)} className="p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"><ChevronRight className="w-5 h-5 text-slate-500" /></button>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 justify-center">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-emerald-500" /><span className="text-[10px] text-slate-400 font-bold">Available</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-amber-500" /><span className="text-[10px] text-slate-400 font-bold">Limited</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-400" /><span className="text-[10px] text-slate-400 font-bold">Full</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-[10px] text-slate-400 font-bold">Your Lesson</span></div>
            </div>

            {/* Calendar grid */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-3">
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS.map(d => <div key={d} className="text-center text-[10px] font-bold text-slate-400 uppercase">{d}</div>)}
              </div>

              {/* Date cells */}
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startDow }).map((_, i) => <div key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const dayData = dayMap[dateStr];
                  const status = dayData?.status || "unavailable";
                  const isToday = dateStr === todayStr;
                  const isPast = dateStr < todayStr;
                  const hasMyBooking = (dayData?.slots || []).some(s => s.status === "my_booking");

                  let dotColor = "bg-transparent";
                  if (status === "open") dotColor = "bg-emerald-500";
                  else if (status === "limited") dotColor = "bg-amber-500";
                  else if (status === "full") dotColor = "bg-red-400";

                  return (
                    <button key={day} data-testid={`cal-day-${day}`}
                      onClick={() => !isPast && status !== "unavailable" && openDay(dateStr)}
                      disabled={isPast || status === "unavailable"}
                      className={`relative aspect-square flex flex-col items-center justify-center rounded-xl transition-all
                        ${isPast ? "opacity-30" : "active:scale-90"}
                        ${isToday ? "ring-2 ring-blue-500" : ""}
                        ${hasMyBooking ? "bg-blue-100 dark:bg-blue-900/20" : "hover:bg-slate-50 dark:hover:bg-slate-700"}
                      `}>
                      <span className={`text-sm font-bold ${isToday ? "text-blue-600" : isPast ? "text-slate-300" : "text-slate-700"}`}>{day}</span>
                      <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${dotColor}`} />
                    </button>
                  );
                })}
              </div>
            </div>

            {!loading && (availabilityError || !hasBookableDay) && (
              <section
                data-testid="booking-availability-empty"
                role={availabilityError ? "alert" : undefined}
                className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
              >
                <p className="text-sm font-extrabold">
                  {availabilityError
                    ? "We couldn't load booking times"
                    : `No online booking times are published for ${MONTHS[month]}.`}
                </p>
                <p className="mt-1 text-xs leading-relaxed">
                  {availabilityError
                    ? availabilityError
                    : "Try another month, or ask your instructor to publish a suitable time."}
                </p>
                {!availabilityError && (
                  <button
                    type="button"
                    onClick={() => nav("/learn/waitlist")}
                    className="mt-3 min-h-11 rounded-xl bg-amber-900 px-4 text-sm font-extrabold text-white dark:bg-amber-200 dark:text-amber-950"
                  >
                    Join the waitlist
                  </button>
                )}
              </section>
            )}

            {/* Upcoming bookings */}
            {calData.filter(d => (d.slots || []).some(s => s.status === "my_booking")).length > 0 && (
              <div>
                <h3 className="text-base font-extrabold text-slate-900 mb-2">Your Bookings</h3>
                <div className="space-y-2">
                  {calData.filter(d => (d.slots || []).some(s => s.status === "my_booking")).map(d => {
                    const mySlots = d.slots.filter(s => s.status === "my_booking");
                    return mySlots.map((s, i) => (
                      <div key={`${d.date}-${i}`} className="bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-2xl p-3.5 flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-bold text-slate-900">{new Date(d.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</p>
                          <p className="text-xs text-slate-400">{fmtTime(s.time)}–{fmtTime(s.end)}</p>
                        </div>
                        <Check className="w-5 h-5 text-blue-500 ml-auto" />
                      </div>
                    ));
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ DAY VIEW ═══ */}
        {view === "day" && selectedDate && (
          <div className="space-y-4">
            {loading ? (
              <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" /></div>
            ) : !selectedDayData || selectedDayData.status === "unavailable" ? (
              <div className="py-12 text-center">
                <p className="text-lg font-extrabold text-slate-300">Not available this day</p>
              </div>
            ) : (
              <>
                {/* Demand indicator */}
                <div className={`rounded-2xl border p-3 text-center ${DEMAND_COLORS[selectedDayData.status]}`}>
                  <p className="text-sm font-bold text-slate-700">
                    {selectedDayData.status === "open" && `${selectedDayData.available} slots available`}
                    {selectedDayData.status === "limited" && `Only ${selectedDayData.available} slot${selectedDayData.available !== 1 ? "s" : ""} left — book soon!`}
                    {selectedDayData.status === "full" && "Fully booked — try another day"}
                    {selectedDayData.status === "approval_required" && "An available time needs instructor approval before it can change the diary"}
                  </p>
                </div>

                {/* Time slots */}
                <div className="space-y-2">
                  {(selectedDayData.slots || []).map((slot, i) => {
                    const cfg = STATUS_COLORS[slot.status] || STATUS_COLORS.gone;
                    const isSelected = selectedSlot?.time === slot.time;
                    const isAvailable = slot.status === "available" || slot.status === "approval_required";
                    return (
                      <button key={i} data-testid={`slot-${slot.time}`}
                        onClick={() => isAvailable && setSelectedSlot(slot)}
                        disabled={!isAvailable}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all
                          ${isSelected ? "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20 border-blue-300" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"}
                          ${isAvailable ? "active:scale-[0.97] hover:bg-slate-50 dark:hover:bg-slate-700" : "opacity-60"}
                        `}>
                        <div className={`w-10 h-10 rounded-xl ${cfg.bg} flex items-center justify-center`}>
                          {slot.status === "my_booking" ? <Check className="w-5 h-5 text-white" /> :
                           slot.status === "gone" ? <X className="w-5 h-5 text-white" /> :
                           <Clock className="w-5 h-5 text-white" />}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-base font-bold text-slate-900">{fmtTime(slot.time)}</p>
                          {slot.status === "approval_required" && (
                            <p className="text-[11px] font-semibold text-amber-700">
                              {slot.exception_kind === "pupil_only_time"
                                ? "Only time you can do? Ask your instructor"
                                : "Adjusted to fit the journey · instructor approval"}
                            </p>
                          )}
                          <p className="text-xs text-slate-400">{cfg.label}</p>
                        </div>
                        {isSelected && <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center"><Check className="w-4 h-4 text-white" /></div>}
                      </button>
                    );
                  })}
                </div>

                {/* Booking form */}
                {selectedSlot && (
                  <div data-testid="booking-form" className="space-y-4">

                    {/* Recommendation banner — always visible */}
                    <div data-testid="rec-banner" className="bg-blue-600 rounded-2xl p-5 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                        <p className="text-sm font-extrabold">Why We Recommend 2 Hours</p>
                      </div>
                      <p className="text-sm leading-relaxed text-blue-100">
                        A <span className="font-extrabold text-white">2-hour lesson</span> gives enough time to travel to your test centre area, complete a proper <span className="font-extrabold text-white">40-minute test route run</span>, receive coaching on weak areas, and return safely — without rushing.
                      </p>
                      <p className="text-xs text-blue-200 mt-2">
                        Shorter lessons may not leave enough time for route practice, especially if you live further from the test centre.
                      </p>

                      {/* "Why 2 Hours?" expandable */}
                      <button data-testid="why-2hrs-toggle" onClick={() => setShowWhyExpanded(!showWhyExpanded)}
                        className="flex items-center gap-1.5 mt-3 text-xs font-bold text-blue-200 hover:text-white transition-colors">
                        <Info className="w-3.5 h-3.5" />
                        <span>{showWhyExpanded ? "Hide details" : "See the full breakdown"}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showWhyExpanded ? "rotate-180" : ""}`} />
                      </button>

                      {showWhyExpanded && (
                        <div data-testid="why-2hrs-expanded" className="mt-3 pt-3 border-t border-blue-500/30 space-y-2.5">
                          <div className="flex items-start gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5"><MapPin className="w-3 h-3 text-white" /></div>
                            <div><p className="text-xs font-bold text-white">Travel time matters</p><p className="text-[11px] text-blue-200">Depending on your location, the drive to your test centre could take 20–40 minutes each way. That time comes out of your lesson.</p></div>
                          </div>
                          <div className="flex items-start gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5"><Clock className="w-3 h-3 text-white" /></div>
                            <div><p className="text-xs font-bold text-white">Test route practice needs ~40 minutes</p><p className="text-[11px] text-blue-200">A proper run through local test routes takes around 40 minutes. This is where you build the confidence and familiarity that helps you pass.</p></div>
                          </div>
                          <div className="flex items-start gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5"><TrendingUp className="w-3 h-3 text-white" /></div>
                            <div><p className="text-xs font-bold text-white">Coaching + safe return</p><p className="text-[11px] text-blue-200">Your instructor needs time to give feedback, work on weak spots, and get you home safely. Rushing this reduces the value of your lesson.</p></div>
                          </div>
                          <div className="bg-blue-500/20 rounded-xl p-3 mt-2">
                            <p className="text-[11px] text-blue-100 leading-relaxed">
                              <span className="font-bold text-white">Example:</span> If travel takes 25 mins each way, that's 50 mins of a 1-hour lesson gone. A 2-hour lesson gives you a full 70 minutes of actual driving time and coaching.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Date/time confirmation */}
                    <div className="flex items-center gap-2 text-sm text-slate-500 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="font-bold text-slate-700">{new Date(selectedDate + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</span>
                      <span className="text-slate-400">at</span>
                      <span className="font-bold text-slate-700">{fmtTime(selectedSlot.time)}</span>
                    </div>

                    {/* Pricing cards */}
                    <div className="space-y-3">
                      <h3 className="text-base font-extrabold text-slate-900">Choose Lesson Length</h3>

                      {/* 2 HOUR — HERO CARD (always first, visually dominant) */}
                      {(() => {
                        const lessons = pricingConfig?.lessons || [
                          { duration: 60, price: 45, label: "1 Hour", subtitle: "Best for local refreshers or focused skill practice", tag: null },
                          { duration: 90, price: 67, label: "90 Minutes", subtitle: "Good for progress lessons with extra driving time", tag: null },
                          { duration: 120, price: 85, label: "2 Hours", subtitle: "Best for test route practice, mock tests, travel time, and faster progress", tag: "best_value" },
                        ];
                        const hero = lessons.find(l => l.duration === 120) || lessons[lessons.length - 1];
                        const others = lessons.filter(l => l.duration !== 120);
                        const isHeroSelected = bookingDuration === hero.duration;
                        const isRec = travelRec?.recommendation?.recommended_duration === hero.duration;

                        return (
                          <>
                            {/* Hero 2-hour card */}
                            <button data-testid="price-120" onClick={() => setBookingDuration(hero.duration)}
                              className={`w-full text-left rounded-2xl border-2 transition-all active:scale-[0.98] relative overflow-hidden
                                ${isHeroSelected
                                  ? "border-blue-500 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 shadow-xl shadow-blue-500/15"
                                  : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300"
                                }`}>
                              <div className={`px-2 py-1 text-center text-[11px] font-extrabold uppercase tracking-wider ${isRec ? "bg-emerald-500 text-white" : "bg-orange-500 text-white"}`}>
                                {isRec ? "Recommended for You" : "Best Value — Most Popular"}
                              </div>
                              <div className="p-5">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-xl font-extrabold text-slate-900">{hero.label}</p>
                                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{hero.subtitle}</p>
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                      {["Test routes", "Mock tests", "Travel time", "Full coaching"].map(t => (
                                        <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full">
                                          <Check className="w-2.5 h-2.5" />{t}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="text-right flex-shrink-0 pl-4">
                                    <p className="text-3xl font-extrabold text-slate-900">£{hero.price}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">£{(hero.price / (hero.duration / 60)).toFixed(2)}/hr</p>
                                  </div>
                                </div>
                              </div>
                              {isHeroSelected && (
                                <div className="absolute top-10 right-5">
                                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center shadow-md"><Check className="w-4 h-4 text-white" /></div>
                                </div>
                              )}
                            </button>

                            {/* Other options — compact */}
                            <div className="grid grid-cols-2 gap-2">
                              {others.map(opt => {
                                const isSel = bookingDuration === opt.duration;
                                return (
                                  <button key={opt.duration} data-testid={`price-${opt.duration}`}
                                    onClick={() => setBookingDuration(opt.duration)}
                                    className={`text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.97] relative
                                      ${isSel
                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/15"
                                        : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
                                      }`}>
                                    <p className="text-base font-extrabold text-slate-900">{opt.label}</p>
                                    <p className="text-xl font-extrabold text-slate-900 mt-1">£{opt.price}</p>
                                    <p className="text-[10px] text-slate-400 mt-1 leading-snug">{opt.subtitle}</p>
                                    {isSel && (
                                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                                        <Check className="w-3 h-3 text-white" />
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Travel recommendation (if postcode checked) */}
                    {travelRec?.recommendation && (
                      <div data-testid="travel-notice" className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                          <TrendingUp className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-emerald-800 dark:text-emerald-400">Based on Your Location</p>
                            <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1 leading-relaxed">{travelRec.recommendation.message}</p>
                            <p className="text-[10px] text-emerald-500/60 mt-1.5">Estimated travel: {travelRec.travel_one_way_mins} mins each way</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Short lesson warning */}
                    {travelRec?.recommendation && bookingDuration < travelRec.recommendation.recommended_duration && (
                      <div data-testid="travel-warning" className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-amber-800 dark:text-amber-400">Shorter lesson selected</p>
                            <p className="text-xs text-amber-600 dark:text-amber-500 mt-1 leading-relaxed">
                              Based on your location, this may reduce available time for test route practice. A longer lesson gives better coaching value.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Postcode check (if not yet checked) */}
                    {!travelRec && (
                      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs text-slate-500 font-bold mb-1.5">Check travel time to your test centre</p>
                            <div className="flex gap-2">
                              <input data-testid="postcode-input" type="text" value={learnerPostcode}
                                onChange={e => setLearnerPostcode(e.target.value.toUpperCase())}
                                placeholder="Your postcode" maxLength={8}
                                className="flex-1 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm font-medium uppercase" />
                              <button data-testid="check-travel" onClick={() => fetchTravelRec(learnerPostcode)}
                                className="px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700">Check</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Cancellation Policy Notice */}
                    <div data-testid="cancellation-notice" className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
                      <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                        <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">Cancellation Policy</h3>
                      </div>
                      <ul className="space-y-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                        <li className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span data-testid="policy-cancel-free">Cancel <span className="font-bold text-slate-800 dark:text-slate-200">up to {cancelHrs} hours before</span> your lesson — choose a full refund or account credit.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <span data-testid="policy-cancel-locked">Cancel <span className="font-bold text-slate-800 dark:text-slate-200">within {cancelHrs} hours</span> — locked. Contact your instructor; they can override if appropriate.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span data-testid="policy-payment-due">Payment is taken automatically <span className="font-bold text-slate-800 dark:text-slate-200">{payHrs} hours before</span> your lesson.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                          <span><span className="font-bold text-slate-800 dark:text-slate-200">No-show</span> — full lesson fee forfeited.</span>
                        </li>
                      </ul>
                    </div>

                    {/* Mandatory Policy Checkbox */}
                    <label data-testid="policy-checkbox-label" className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative mt-0.5">
                        <input data-testid="policy-checkbox" type="checkbox" checked={policyAccepted}
                          onChange={(e) => setPolicyAccepted(e.target.checked)}
                          className="sr-only peer" />
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all
                          ${policyAccepted
                            ? "bg-blue-600 border-blue-600"
                            : "border-slate-300 dark:border-slate-600 group-hover:border-blue-400"
                          }`}>
                          {policyAccepted && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>
                      <span className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed" data-testid="policy-checkbox-text">
                        I understand lessons can be cancelled freely until <span className="font-bold text-slate-800 dark:text-slate-200">{cancelHrs} hours</span> before the lesson start time; inside that window the booking is locked.
                      </span>
                    </label>

                    {/* Payment Options — pay now OR book without paying */}
                    <div data-testid="payment-section" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        <h3 className="text-base font-extrabold text-slate-900 dark:text-white">How would you like to pay?</h3>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Pay now with card to confirm immediately, or book first and settle with your instructor later.
                      </p>
                    </div>

                    {/* R1.2 — Always-visible price breakdown driven by the
                        backend /quote endpoint. Renders Lesson + (optional)
                        Platform transaction fee + Total so the pupil sees
                        the real charge BEFORE Stripe. */}
                    {quote && (
                      <div data-testid="booking-breakdown" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex items-center justify-between text-sm">
                          <span data-testid="bd-lesson-label" className="text-slate-600 dark:text-slate-400">Lesson price</span>
                          <span data-testid="bd-lesson-amount" className="font-bold text-slate-900 dark:text-white">£{(quote.base_amount_pence / 100).toFixed(2)}</span>
                        </div>
                        {quote.booking_fee_pence > 0 && (
                          <div className="relative flex items-center justify-between text-sm mt-1.5">
                            <span data-testid="bd-fee-label" className="text-slate-600 dark:text-slate-400 inline-flex items-center gap-1.5">
                              Platform transaction fee
                              {quote.platform_fee_bps ? ` (${(quote.platform_fee_bps / 100).toFixed(2).replace(/\.?0+$/, "")}%)` : ""}
                              <button
                                type="button"
                                data-testid="bd-fee-why"
                                aria-label="Why this fee?"
                                aria-expanded={showFeeTooltip}
                                onClick={() => setShowFeeTooltip(v => !v)}
                                onBlur={() => setShowFeeTooltip(false)}
                                className="ml-0.5 inline-flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-full"
                              >
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </span>
                            <span data-testid="bd-fee-amount" className="font-bold text-slate-900 dark:text-white">+£{(quote.booking_fee_pence / 100).toFixed(2)}</span>
                            {showFeeTooltip && (
                              <div
                                data-testid="bd-fee-tooltip"
                                role="tooltip"
                                className="absolute left-0 top-full mt-1 z-10 max-w-xs px-3 py-2 rounded-xl bg-slate-900 text-white text-[11px] leading-relaxed shadow-lg dark:bg-slate-100 dark:text-slate-900"
                              >
                                <p className="font-bold mb-0.5">Why this fee?</p>
                                <p>Funds secure payments, dispute protection, and platform services.</p>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                          <span className="text-sm font-extrabold text-slate-900 dark:text-white">Total</span>
                          <span data-testid="bd-total-amount" className="text-base font-extrabold text-blue-600">£{(quote.pupil_total_pence / 100).toFixed(2)}</span>
                        </div>
                      </div>
                    )}

                    {/* Pay now — primary Stripe flow */}
                    <Button data-testid="confirm-booking" onClick={bookSlot}
                      disabled={!policyAccepted || checkoutLoading || payLaterLoading}
                      className={`w-full h-16 rounded-2xl shadow-lg transition-all
                        ${policyAccepted
                          ? "bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/25"
                          : "bg-slate-300 dark:bg-slate-700 text-slate-500 dark:text-slate-400 cursor-not-allowed shadow-none"
                        }`}>
                      <div className="text-center">
                        {checkoutLoading ? (
                          <div className="flex items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <p className="text-base font-extrabold">Redirecting to payment...</p>
                          </div>
                        ) : (
                          <>
                            <p className="text-base font-extrabold">
                              {policyAccepted ? "Pay now and confirm booking" : "Accept Policy to Continue"}
                            </p>
                            <p className={`text-xs mt-0.5 ${policyAccepted ? "text-blue-200" : "text-slate-400"}`}>
                              {(() => {
                                const opt = (pricingConfig?.lessons || [{ duration: 60, price: 45 }, { duration: 90, price: 67 }, { duration: 120, price: 85 }]).find(l => l.duration === bookingDuration);
                                const durLabel = bookingDuration === 60 ? "1 Hour" : bookingDuration === 90 ? "90 Minutes" : "2 Hours";
                                // R1.2 — when the pupil covers the booking
                                // fee, surface the real total so the button
                                // text matches the breakdown above.
                                const totalP = quote?.pupil_total_pence != null
                                  ? `£${(quote.pupil_total_pence / 100).toFixed(2)}`
                                  : `£${opt?.price || 45}`;
                                return `${durLabel} — ${totalP} — Secure card payment`;
                              })()}
                            </p>
                          </>
                        )}
                      </div>
                    </Button>

                    {/* Book without paying now — secondary, same policy gate */}
                    <button
                      data-testid="book-pay-later"
                      onClick={bookSlotPayLater}
                      disabled={!policyAccepted || checkoutLoading || payLaterLoading}
                      className={`w-full h-14 rounded-2xl border-2 font-extrabold text-sm transition-all
                        ${policyAccepted
                          ? "border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-white hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/15"
                          : "border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed"
                        }`}>
                      {payLaterLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" /> Booking…
                        </span>
                      ) : (
                        <span className="flex flex-col items-center justify-center leading-tight">
                          <span>Book lesson without paying now</span>
                          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 mt-0.5">
                            Pay your instructor later · marked Payment due
                          </span>
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
